import { useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { Stack, ToggleButton, ToggleButtonGroup } from '@rossum/ui/material';
import { useScoreThresholdSlider } from './useScoreThresholdSlider';

const DEFAULT_VALUE = 0.8;

const predefinedValues = [
  { value: 0, label: '0 %' },
  { value: 0.65, label: '65 %' },
  { value: DEFAULT_VALUE, label: '80 %' },
  { value: 0.975, label: '97.5 %' },
  { value: 1, label: '100 %' },
];

// While the user didn't leave the custom input, the value could be incorrect
// so we need to do handle it here
export const clampThresholdValue = (value: number) =>
  Math.max(0, Math.min(value, 1));

const sliderProps = {
  sx: {
    maxWidth: 240,
  },
};

const DefaultScoreThreshold = ({
  control,
  defaultValue,
}: {
  control: Control<Pick<Queue, 'defaultScoreThreshold' | 'automationLevel'>>;
  defaultValue: number;
}) => {
  const intl = useIntl();

  const {
    field: { value: thresholdValue, onChange: thresholdOnChange },
  } = useController({ name: 'defaultScoreThreshold', control });

  const [isCustom, setIsCustom] = useState(
    !predefinedValues.some(option => option.value === thresholdValue)
  );

  const { Component: ScoreThresholdSlider } = useScoreThresholdSlider({
    defaultValue,
    thresholdValue,
    onChange: thresholdOnChange,
    sliderProps,
  });

  return (
    <Stack gap={2}>
      <ToggleButtonGroup
        value={isCustom ? 'custom' : thresholdValue}
        onChange={(_e, value) => thresholdOnChange(value)}
        exclusive
        size="large"
        sx={{ alignSelf: 'flex-start' }}
      >
        {predefinedValues.map(({ value, label }) => (
          <ToggleButton
            key={value}
            value={value}
            onClick={() => {
              setIsCustom(false);
            }}
          >
            {label}
          </ToggleButton>
        ))}
        <ToggleButton
          value="custom"
          onClick={e => {
            e.preventDefault();
            setIsCustom(true);
          }}
        >
          {intl.formatMessage({ id: 'components.threshold.custom' })}
        </ToggleButton>
      </ToggleButtonGroup>
      {isCustom ? ScoreThresholdSlider : null}
    </Stack>
  );
};

export default DefaultScoreThreshold;
